/**
 * Flagship-resort deep dive (Japan: Furano): one resort a site covers in depth
 * with a guide page, a travel guide and an FAQ, mounted at
 * /:lang/<SiteConfig.routes.flagship.slug>/{,travel-guide/,faq/}.
 *
 * Data: sites/<id>/data/flagship.ts exports FLAGSHIP (null in the fallback,
 * and then the pages are not routed). Every text block is keyed by language;
 * a missing language falls back to English (pick()).
 */
import type { Language } from '../types/resort';
import { FLAGSHIP as SITE_FLAGSHIP } from '@site/data/flagship';

/** A value per language; `en` is the fallback. */
export type ByLanguage<T> = Partial<Record<Language, T>> & { en: T };

export interface FlagshipLink {
  /** Path without language prefix, e.g. /furano/faq */
  to: string;
  label: string;
  /** plane | ticket | camera | calendar | hotel | utensils */
  icon?: string;
}

export interface FlagshipFaqItem {
  q: string;
  a: string;
}

export interface FlagshipMainCopy {
  title: string;
  description: string;
  keywords: string;
  h1: string;
  lede: string;
  factsHeading: string;
  facts: { label: string; value: string }[];
  zonesHeading: string;
  zones: { name: string; body: string }[];
  snowHeading: string;
  snowBody: string;
  ticketHeading: string;
  ticketBody: string;
  accessHeading: string;
  accessBody: string;
  linksHeading: string;
  links: FlagshipLink[];
  faq: FlagshipFaqItem[];
}

export interface DeepDiveSection {
  id: string;
  heading: string;
  body: string;
  bullets?: string[];
}

export interface DeepDiveCopy {
  heading: string;
  intro: string;
  sections: DeepDiveSection[];
}

export interface FlagshipGuideCopy {
  title: string;
  description: string;
  keywords: string;
  h1: string;
  lede: string;
  sections: { icon: string; heading: string; body: string[] }[];
  linksHeading: string;
  links: FlagshipLink[];
}

export interface FlagshipFaqCopy {
  title: string;
  description: string;
  keywords: string;
  h1: string;
  lede: string;
  faq: FlagshipFaqItem[];
}

export interface FlagshipData {
  /** Resort the deep dive is about (its /resort/<slug> page). */
  resortSlug: string;
  /** Short name for breadcrumbs, e.g. Furano. */
  name: ByLanguage<string>;
  /** Full resort name for JSON-LD, e.g. Furano Ski Resort. */
  resortName: ByLanguage<string>;
  /** Badge above the H1, e.g. "Hokkaido · Furano". */
  badge: ByLanguage<string>;
  /** JSON-LD address line and region. */
  address: string;
  region: string;
  latitude: number;
  longitude: number;
  /** Link labels on the FAQ page. */
  faqRelated: {
    resortGuide: ByLanguage<string>;
    travelGuide: ByLanguage<string>;
    countryFaq: ByLanguage<string>;
  };
  main: ByLanguage<FlagshipMainCopy>;
  deepDive: ByLanguage<DeepDiveCopy>;
  guide: ByLanguage<FlagshipGuideCopy>;
  faq: ByLanguage<FlagshipFaqCopy>;
}

export const FLAGSHIP: FlagshipData | null = SITE_FLAGSHIP;
